import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Phone, MessageCircle, X } from 'lucide-react';
import { ANIMATION_CONFIG } from '@/config/animations';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';

export function StickyCTABar() {
  const { t } = useTranslation('common');
  const [isVisible, setIsVisible] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);

  useEffect(() => {
    // Check if CTA was already clicked in this session
    if (sessionStorage.getItem(ANIMATION_CONFIG.stickyBar.sessionStorageKey)) {
      setIsDismissed(true);
      return;
    }

    // Check if animations are enabled
    if (!ANIMATION_CONFIG.enabled.stickyBar) {
      return;
    }

    const handleScroll = () => {
      const scrollPercent = window.scrollY / (document.documentElement.scrollHeight - window.innerHeight);
      
      if (scrollPercent >= ANIMATION_CONFIG.scroll.stickyBarThreshold && !isDismissed) {
        setIsVisible(true);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isDismissed]);

  const handleCTAClick = () => {
    sessionStorage.setItem(ANIMATION_CONFIG.stickyBar.sessionStorageKey, 'true');
    setIsVisible(false);
    setIsDismissed(true);
  };

  const handleDismiss = () => {
    setIsVisible(false);
    setIsDismissed(true);
  };

  // Check for reduced motion preference
  const prefersReducedMotion = typeof window !== 'undefined' 
    ? window.matchMedia('(prefers-reduced-motion: reduce)').matches 
    : false;

  if (isDismissed || !ANIMATION_CONFIG.enabled.stickyBar) return null;

  const whatsappMessage = t('stickyBar.whatsappMessage');

  return (
    <AnimatePresence>
      {isVisible && (
          <motion.div
            initial={prefersReducedMotion ? { opacity: 0 } : { y: 100, opacity: 0 }}
            animate={prefersReducedMotion ? { opacity: 1 } : { y: 0, opacity: 1 }}
            exit={prefersReducedMotion ? { opacity: 0 } : { y: 100, opacity: 0 }}
            transition={{ 
              type: 'spring', 
              damping: 25, 
              stiffness: 300,
              duration: ANIMATION_CONFIG.durations.slow / 1000 
            }}
            className="fixed bottom-0 left-0 right-0 z-50 p-3 md:p-6"
            style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 12px)' }}
          >
            <div className="container mx-auto">
              <div className="relative bg-primary/95 backdrop-blur-xl rounded-xl md:rounded-2xl shadow-2xl border border-primary-foreground/10 p-3 md:p-6">
              {/* Dismiss button */}
              <button
                onClick={handleDismiss}
                className="absolute top-3 left-3 p-1.5 rounded-lg text-primary-foreground/60 hover:text-primary-foreground hover:bg-primary-foreground/10 transition-colors"
                aria-label={t('stickyBar.dismissAriaLabel')}
              >
                <X className="w-5 h-5" />
              </button>

              <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                {/* Text */}
                <div className="text-center md:text-right">
                  <p className="text-primary-foreground font-heading font-bold text-base md:text-xl">
                    {t('stickyBar.text')}
                  </p>
                </div>

                {/* Buttons */}
                <div className="flex flex-row gap-2 sm:gap-3">
                  <Link to="/contact" onClick={handleCTAClick} className="flex-1 sm:flex-none">
                    <Button variant="cta" size="default" className="gap-1.5 sm:gap-2 w-full text-sm md:text-base px-3 md:px-6">
                      <Phone className="w-4 h-4 md:w-5 md:h-5" />
                      <span className="hidden sm:inline">{t('stickyBar.primaryButton')}</span>
                      <span className="sm:hidden">צור קשר</span>
                    </Button>
                  </Link>
                  <a
                    href={`https://wa.me/${ANIMATION_CONFIG.stickyBar.whatsappNumber}?text=${encodeURIComponent(whatsappMessage)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={handleCTAClick}
                    className="flex-1 sm:flex-none"
                  >
                    <Button variant="heroOutline" size="default" className="gap-1.5 sm:gap-2 w-full text-sm md:text-base px-3 md:px-6">
                      <MessageCircle className="w-4 h-4 md:w-5 md:h-5" />
                      <span className="hidden sm:inline">{t('stickyBar.secondaryButton')}</span>
                      <span className="sm:hidden">וואטסאפ</span>
                    </Button>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
